import type { ArtifactVersion, LocationSnapshot } from '@der-maschinfuehrer/contracts';
import { useEffect, useState } from 'react';

import type { DraftEndpointName, DraftEndpointUpdateValue } from '../../domain/alpha-shift';
import type { AppStorage } from '../../storage/app-storage';

interface RecentLocation {
  readonly catalogDocumentId: string | null;
  readonly catalogVersion: ArtifactVersion | null;
  readonly lastUsedAt: number;
  readonly snapshot: LocationSnapshot;
}

export function RecentLocationsPanel({
  disabled,
  onSelect,
  storage,
}: {
  readonly disabled: boolean;
  readonly onSelect: (
    endpointName: Extract<DraftEndpointName, 'origin' | 'destination'>,
    value: DraftEndpointUpdateValue,
  ) => void;
  readonly storage: AppStorage;
}) {
  const [locations, setLocations] = useState<readonly RecentLocation[] | null>(null);
  const [selectedId, setSelectedId] = useState('');

  useEffect(() => {
    let active = true;
    void Promise.all([storage.listShifts(), storage.listUserDefinedLocations()]).then(
      ([shifts, userDefinedLocations]) => {
        if (!active) return;
        const byId = new Map<string, RecentLocation>();
        for (const aggregate of shifts) {
          for (const segment of aggregate.segments) {
            if (segment.metadata.tombstone !== null) continue;
            const lastUsedAt = Date.parse(segment.metadata.updated_at);
            for (const snapshot of [segment.origin, segment.destination]) {
              if (
                snapshot === null ||
                (snapshot.country_code !== 'CZ' && snapshot.country_code !== 'DE')
              ) {
                continue;
              }
              const documentId = catalogDocumentId(snapshot);
              const catalogVersion = documentId === null ? null : segment.data_versions.catalog;
              const candidate: RecentLocation = {
                catalogDocumentId: catalogVersion === null ? null : documentId,
                catalogVersion,
                lastUsedAt,
                snapshot,
              };
              const current = byId.get(snapshot.location_id);
              if (current === undefined || candidate.lastUsedAt > current.lastUsedAt) {
                byId.set(snapshot.location_id, candidate);
              }
            }
          }
        }
        // A just-undone segment is intentionally absent from live history, but
        // its durable user-defined locations remain valid reusable identities.
        // Add those only as a fallback so a live immutable trip snapshot always
        // wins over a later mutable location entity.
        for (const location of userDefinedLocations) {
          if (
            byId.has(location.location_id) ||
            (location.country_code !== 'CZ' && location.country_code !== 'DE')
          ) {
            continue;
          }
          const snapshot: LocationSnapshot = {
            location_id: location.location_id,
            display_name: location.official_name,
            location_type: location.location_type,
            country_code: location.country_code,
            key_identifiers: location.identifiers.map((identifier) => ({
              namespace: identifier.namespace,
              namespace_other: identifier.namespace_other,
              value: identifier.value,
            })),
          };
          byId.set(location.location_id, {
            catalogDocumentId: null,
            catalogVersion: null,
            lastUsedAt: Date.parse(location.metadata.updated_at),
            snapshot,
          });
        }
        const next = [...byId.values()]
          .sort((left, right) => right.lastUsedAt - left.lastUsedAt)
          .slice(0, 12);
        setLocations(next);
        setSelectedId((current) => current || next[0]?.snapshot.location_id || '');
      },
      () => {
        if (active) setLocations([]);
      },
    );
    return () => {
      active = false;
    };
  }, [storage]);

  if (locations === null) return <p role="status">Načítám nedávná místa…</p>;
  if (locations.length === 0) return null;
  const selected = locations.find((location) => location.snapshot.location_id === selectedId);
  const apply = (endpointName: 'origin' | 'destination') => {
    if (selected === undefined) return;
    onSelect(endpointName, recentLocationValue(selected));
  };
  return (
    <aside className="recent-locations" aria-label="Nedávná provozní místa">
      <label className="form-field">
        <span>Nedávné místo</span>
        <select
          className="text-input touch-target"
          disabled={disabled}
          value={selectedId}
          onChange={(event) => setSelectedId(event.currentTarget.value)}
        >
          {locations.map((location) => (
            <option key={location.snapshot.location_id} value={location.snapshot.location_id}>
              {location.snapshot.display_name} · {location.snapshot.country_code}
              {location.catalogDocumentId === null ? ' · vlastní / historické' : ' · katalog'}
            </option>
          ))}
        </select>
      </label>
      <div className="inline-actions">
        <button
          type="button"
          className="secondary-action touch-target"
          disabled={disabled}
          onClick={() => apply('origin')}
        >
          Použít jako Odkud
        </button>
        <button
          type="button"
          className="secondary-action touch-target"
          disabled={disabled}
          onClick={() => apply('destination')}
        >
          Použít jako Kam
        </button>
      </div>
    </aside>
  );
}

function recentLocationValue(location: RecentLocation): DraftEndpointUpdateValue {
  return {
    name: location.snapshot.display_name,
    country_code: location.snapshot.country_code as 'CZ' | 'DE',
    key_identifier: location.snapshot.key_identifiers[0] ?? null,
    carried_location_snapshot: location.snapshot,
    ...(location.catalogDocumentId === null || location.catalogVersion === null
      ? {}
      : {
          catalog_document_id: location.catalogDocumentId,
          catalog_version: location.catalogVersion,
        }),
  };
}

function catalogDocumentId(snapshot: LocationSnapshot): string | null {
  return (
    snapshot.key_identifiers.find(
      (identifier) => identifier.namespace === 'OTHER' && identifier.namespace_other === 'OSM',
    )?.value ?? null
  );
}
